import { ChevronRightSmall } from "src/icons/ChevronRightSmall";
import {
  ConsentItem,
  ConsentItemDescription,
  ConsentItemHeader,
  ConsentItemTitle,
  ConsentList,
} from "../ConsentManagerBanner.styled";
import { categories, Categories } from "../utils";
import { Toggle } from "src/atoms/Toggle";
import { Expandable } from "src/atoms/Expandable";
import {
  CategoryPreferences,
  Destination,
} from "@ht-sdks/consent-manager/types/types";
import { ReactNode, useCallback, useState } from "react";
import { Trans } from "@lingui/macro";

type SectionProps = {
  displayToggle: boolean;
  name: ReactNode;
  description: ReactNode;
  disabled: boolean;
};

const displayedSections: Record<Categories, SectionProps> = {
  functional: {
    displayToggle: true,
    name: <Trans>consent_manager.functional_title</Trans>,
    description: <Trans>consent_manager.functional_body</Trans>,
    disabled: true,
  },
  analytics: {
    displayToggle: true,
    name: <Trans>consent_manager.analytics_title</Trans>,
    description: <Trans>consent_manager.analytics_body</Trans>,
    disabled: false,
  },
  marketing: {
    displayToggle: true,
    name: <Trans>consent_manager.marketing_title</Trans>,
    description: <Trans>consent_manager.marketing_body</Trans>,
    disabled: false,
  },
} as const;

const ConsentSettings = ({
  destinations,
  preferences,
  setPreferences,
  setListExpanded,
}: {
  destinations: Destination[];
  preferences: CategoryPreferences;
  setPreferences: (newPreferences: CategoryPreferences) => void;
  setListExpanded: (expanded: boolean) => void;
}): JSX.Element => {
  const [expanded, setExpanded] = useState(
    categories.reduce((map, section) => {
      map[section] = false;
      return map;
    }, {} as Record<Categories | "essentials", boolean>)
  );

  const handleClick = useCallback((sectionType: Categories) => {
    setExpanded((map) => ({
      ...map,
      [sectionType]: !map[sectionType],
    }));
    setListExpanded(true);
  }, []);

  return (
    <ConsentList>
      {categories.map((sectionType) => {
        const section = displayedSections[sectionType];
        return (
          <ConsentItem key={sectionType}>
            <ConsentItemHeader>
              <ConsentItemTitle
                aria-controls={`consent-section-${sectionType}`}
                aria-expanded={expanded[sectionType]}
                expanded={expanded[sectionType]}
                onClick={() => handleClick(sectionType)}
              >
                <ChevronRightSmall />
                <span>{section.name}</span>
              </ConsentItemTitle>
              {section.displayToggle && (
                <Toggle
                  name={sectionType}
                  checked={!!preferences[sectionType]}
                  disabled={section.disabled}
                  onChange={({ target }) => {
                    setPreferences({
                      [target.name]: target.checked,
                    });
                  }}
                />
              )}
            </ConsentItemHeader>
            <Expandable
              id={`consent-section-${sectionType}`}
              isExpanded={expanded[sectionType]}
            >
              <ConsentItemDescription>
                {section.description}
              </ConsentItemDescription>
            </Expandable>
          </ConsentItem>
        );
      })}
    </ConsentList>
  );
};

export default ConsentSettings;
